import { useState, useEffect, useRef } from 'react';
import {TryParseIntDefault, TryParseFloatDefault, RandomId, CheckImageUrlExists}  from './helpers';
import {IconButton, Paper, Box, Stack, Button, Chip, Popover, Typography, } from '@mui/material';
import { Icon as Iconify } from '@iconify/react';

import Konva from "konva";
import { ccColorDarkTeal, ccColorLightTeal, ccColorOrange } from './helpersStyles';
import { CcActionButton } from '../commonComponents/styledTexts';
import { LionColorPicker } from './lionColorPicker';
import { CcHorizontalSlider, InputNumber } from '../commonComponents/inputFields';
import { lionIcons } from './helpersIcons';

function TextEffectExample(props) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    const stage = new Konva.Stage({
      container: containerRef.current,
      width: 320,
      height: 200
    });

    const layer = new Konva.Layer();

    let text = undefined
    switch (props.effect.effectName) {
        case "dropShadow":
            text = new Konva.Text({
            x: 0,
            y: 76,
            width: 320,
            text: "Drop shadow",
            fontSize: 48,
            fontFamily: "Arial",
            fontStyle: "bold",
            fill: ccColorOrange,
            align: 'center',

            });
            break
        default:
            break;

    }

    if (text) {
        applyTextEffectToKonvaNode(text, props.effect)
        layer.add(text);
    }
    stage.add(layer);

    stageRef.current = stage;

    return () => {
      stage.destroy();
      stageRef.current = null;
    };
  }, []);

  return <div ref={containerRef} />;
}
const TextGlassEffectExample = (props) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    const width = 320;
    const height = 200;

    const stage = new Konva.Stage({
      container: containerRef.current,
      width: width,
      height: height
    });

    const layer = new Konva.Layer();
    stage.add(layer);

    const img = new Image();
    img.src = "/assets/effects/t01.jpg";

    const bgLayer = new Konva.Layer();
    const glassLayer = new Konva.Layer();

    stage.add(bgLayer);
    stage.add(glassLayer);

    //img.src = "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1200";

    img.onload = () => {
      // Full sharp background
      const background = new Konva.Image({
        image: img,
        x: 0,
        y: 0,
        width,
        height
      });

      bgLayer.add(background);
      bgLayer.draw();

      const textConfig = {
        x: 10,
        y: 70,
        width: width - 20,
        text: "GLASS",
        fontSize: 78,
        fontFamily: "Arial Black",
        fontStyle: "bold",
        align: "center"
      };

      // Group that contains blurred background clipped to text
      const glassGroup = new Konva.Group({
        x: 0,
        y: 0
      });

      const blurredBackground = new Konva.Image({
        image: img,
        x: 0,
        y: 0,
        width,
        height
      });

      blurredBackground.cache();
      blurredBackground.filters([Konva.Filters.Blur]);
      blurredBackground.blurRadius(16);

      const textMask = new Konva.Text({
        ...textConfig,
        fill: "black",
        globalCompositeOperation: "destination-in"
      });

      glassGroup.add(blurredBackground);
      glassGroup.add(textMask);

      // This isolates destination-in so it does not erase the main background
      glassGroup.cache({
        x: 0,
        y: 0,
        width,
        height
      });

      glassLayer.add(glassGroup);

      // Soft dark shadow
      const shadowText = new Konva.Text({
        ...textConfig,
        fill: "rgba(0,0,0,0.35)",
        x: textConfig.x,
        y: textConfig.y + 8,
        shadowColor: "black",
        shadowBlur: 20,
        shadowOpacity: 0.35
      });

      glassLayer.add(shadowText);
      shadowText.moveToBottom();

      // Glass tint and border
      const glassText = new Konva.Text({
        ...textConfig,
        fill: "rgba(255,255,255,0.25)",
        stroke: "rgba(255,255,255,0.85)",
        strokeWidth: 2,
        shadowColor: "black",
        shadowBlur: 14,
        shadowOffset: { x: 0, y: 6 },
        shadowOpacity: 0.25,
        fillAfterStrokeEnabled: true
      });

      glassLayer.add(glassText);

      // Small top-left highlight
      const highlightText = new Konva.Text({
        ...textConfig,
        x: textConfig.x - 2,
        y: textConfig.y - 2,
        fill: "rgba(255,255,255,0.05)"
      });

      glassLayer.add(highlightText);

      // Optional extra shine
      const shineText = new Konva.Text({
        ...textConfig,
        fill: "rgba(255,255,255,0.16)",
        globalCompositeOperation: "screen"
      });

      glassLayer.add(shineText);

      glassLayer.draw();
    }
    stageRef.current = stage;

    return () => {
      stage.destroy();
      stageRef.current = null;
    };

  }, [])

  return <div ref={containerRef} />;
}
const TextBevelEffectExample = (props) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);



  useEffect(() => {
    const width = 320;
    const height = 200;

    const stage = new Konva.Stage({
      container: containerRef.current,
      width: width,
      height: height
    });

    const layer = new Konva.Layer();
    stage.add(layer);


    addBeveledExtrudedText(layer, undefined, {
      text: "BEVEL",
      x: 10,
      y: 55,
      fontSize: 86,

      fill: "#d9a441",
      stroke: "#6f3f08",
      strokeWidth: 2,

      bevelDepth: 5, // edge thickness typically 2-6
      bevelExtrusionDepth: 18, // 3D body depth typically 0-30
      bevelLightAngle: -45,

      bevelHighlightColor: "255,255,255",
      bevelShadowColor: "80,40,0",
      bevelExtrusionColor: "110,65,15"
    });



    layer.draw();

    stageRef.current = stage;

    return () => {
      stage.destroy();
      stageRef.current = null;
    };

  }, [])

  return <div ref={containerRef} />;
}
function TextNodePreview(props) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    //console.log(props.effects)
    const stage = new Konva.Stage({
      container: containerRef.current,
      width: props.node.width,
      height: props.node.height
    });

    const layer = new Konva.Layer();
    const previewText = {...props.node}
    clearTextEffectsFromKonvaNode(previewText)
    //console.log(previewText)


    
    let text = undefined
    for (const eff of props.effects) {
      if (eff.effectName === 'bevel') {
        addBeveledExtrudedText(layer, {...previewText, x: 0, y: 0}, eff)

      }
      else {
        text = new Konva.Text({...previewText, x: 0, y: 0});
        applyTextEffectToKonvaNode(text, eff)
      }
    }

/*
    if (text) {
        applyTextEffectToKonvaNode(text, props.effect)
        layer.add(text);
    }
*/

    if (text) layer.add(text);
    stage.add(layer);

    stageRef.current = stage;

    return () => {
      stage.destroy();
      stageRef.current = null;
    };
  }, [props.node, props.effects]);

  return <div ref={containerRef} />;
}

export const applyTextEffectToKonvaNode = (node, effectMetadata) => {
    node.setAttrs(effectMetadata)
    /*
    Object.keys(effectMetadata).forEach(key => {
        if (key !== 'effectName') console.log(key, effectMetadata[key]);
    });
    */
}
export const clearTextEffectsFromKonvaNode = (node) => {
  if (node.textEffects == undefined) return
  for (const eff of node.textEffects) {
    const effKeys = Object.keys(eff)
    for (const k of effKeys) delete node[k]
  }
}
export function addBeveledExtrudedText(layer, node, effectMetadata) {
    const {
      x = 0,
      y = 0,
      text = 'Bevel',

      fill = "#d9a441",
      stroke = "#7a4c10",
      strokeWidth = 2,

      bevelDepth = 4,
      bevelExtrusionDepth = 12,
      bevelLightAngle = -45,

      bevelHighlightColor = "255,255,255",
      bevelShadowColor = "70,35,0",
      bevelExtrusionColor = "90,50,10"
    } = effectMetadata;

    const angle = bevelLightAngle * Math.PI / 180;
    const lx = Math.cos(angle);
    const ly = Math.sin(angle);

    /*
    const fullProps = {...node, fill, stroke, strokeWidth, 
      bevelDepth, bevelExtrusionDepth, bevelLightAngle,
    bevelHighlightColor, bevelShadowColor, bevelExtrusionColor, x, y, text}
*/

    const fullProps = {...node, ...effectMetadata}
    console.log(fullProps)

    function makeText(extra) {
      return new Konva.Text({
        id: `bevel_${RandomId(20)}`,
        ...fullProps,
        listening: false,
        ...extra,
      });
    }

    // 1. Extrusion / 3D body
    // Drawn away from the light source.
    for (let i = bevelExtrusionDepth; i >= 1; i--) {
      const t = i / bevelExtrusionDepth;

      layer.add(makeText({
        x: x - lx * i,
        y: y - ly * i,
        fill: `rgba(${bevelExtrusionColor},${0.55 + 0.35 * t})`
      }));
    }

    // 2. Dark bevel side
    // Short gradient away from the light.
    for (let i = bevelDepth; i >= 1; i--) {
      const t = i / bevelDepth;

      layer.add(makeText({
        x: x - lx * i,
        y: y - ly * i,
        fill: `rgba(${bevelShadowColor},${0.08 + 0.22 * t})`
      }));
    }

    // 3. Light bevel side
    // Short gradient toward the light.
    for (let i = bevelDepth; i >= 1; i--) {
      const t = i / bevelDepth;

      layer.add(makeText({
        x: x + lx * i,
        y: y + ly * i,
        fill: `rgba(${bevelHighlightColor},${0.06 + 0.20 * t})`
      }));
    }

    // 4. Outer stroke / edge definition
    layer.add(makeText({
      fill,
      stroke,
      strokeWidth
    }));

    // 5. Main text face
    layer.add(makeText({
      fill
    }));
}

export const TextEffectsManager = (props) => {
    const effectsGallery = [{effectName: 'dropShadow', shadowColor: "#000000",
            shadowOpacity: 0.5, shadowOffsetX: 4, shadowOffsetY: 6,
            shadowBlur: 8},
      {effectName: 'bevel',
      x: 0, y: 0,

      fill: "#d9a441",
      stroke: "#6f3f08",
      strokeWidth: 2,

      bevelDepth: 5, // edge thickness typically 2-6
      bevelExtrusionDepth: 18, // 3D body depth typically 0-30
      bevelLightAngle: -45,

      bevelHighlightColor: "255,255,255",
      bevelShadowColor: "80,40,0",
      bevelExtrusionColor: "110,65,15"
    }            
          ]

  useEffect(() => {
    if (props.currentBlock?.textEffects !== undefined) setTextEffects(props.currentBlock.textEffects)
    else setTextEffects([])
  }, [props.currentBlock])


  const [textEffects, setTextEffects] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const openBuilder = Boolean(anchorEl);
  const handleOpenBuilder = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseBuilder = () => {
    if (props.onTextFunctionsChange !== undefined) props.onTextFunctionsChange(textEffects);

    setAnchorEl(null);
    
  };

  const getLabelTitle = (effects) => {
    if (effects == undefined || effects.length === 0) return 'none'

    return 'effect applied'
  }

  const addEffect = (eff) => {
    //MOD: we no longer support multiple effects
    //there is max 1 effect so the array can only have 1 element max
    //const newEffects = [...textEffects]
    //newEffects.push(eff)

    const newEffects = [eff]
    setTextEffects(newEffects)

  }
  const removeEffect = (eff) => {
    const newEffects = [...textEffects]
    const index = newEffects.findIndex(newEff => newEff.effectName === eff.effectName)
    newEffects.splice(index, 1)
    setTextEffects(newEffects)
  }

  const effectIsUsed = (eff) => {
    const effectFound = textEffects.find(tEff => tEff.effectName === eff.effectName)
    if (effectFound) return true
    else return false
  }

  const renderUIForEffect = (effect) => {
    switch (effect.effectName)
    {
        case "dropShadow":
            return <div className='text-gray-700 text-sm font-semibold mt-2'>
                    <div className='flex flex-row gap-x-2'>
                      <div>
                        <div>Shadow colour</div>
                        <LionColorPicker color={effect.shadowColor}      
                            onChange={(color) => handleTextEffectChange(effect, 'shadowColor', color)}
                          />
                      </div>
                      <div>
                        <div>Shadow opacity</div>
                        <CcHorizontalSlider min={0} max={1} 
                              value={effect.shadowOpacity}
                              onChange={val => handleTextEffectChange(effect, 'shadowOpacity', val)}
                              />
                      </div>
                    </div>
                    <div className='flex flex-row gap-x-2 mt-2'>
                      <div>
                        <div>Shadow offsetX</div>
                        <div className='w-32'>
                            <InputNumber displayName="" small
                              min={0} max={100} incr={1}
                              onChange={val => handleTextEffectChange(effect, 'shadowOffsetX', val)}
                              value={effect.shadowOffsetX}
                            />
                        </div>
                      </div>
                      <div>
                        <div>Shadow offsetY</div>
                        <div className='w-32'>
                            <InputNumber displayName="" small
                              min={0} max={100} incr={1}
                              onChange={val => handleTextEffectChange(effect, 'shadowOffsetY', val)}
                              value={effect.shadowOffsetY}
                            />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div>Shadow blur</div>
                      <div className='w-32'>
                          <InputNumber displayName="" small
                            min={0} max={100} incr={1}
                            onChange={val => handleTextEffectChange(effect, 'shadowBlur', val)}
                            value={effect.shadowBlur}
                          />
                      </div>
                    </div>
              
            </div>
        break
        default:
            return <></>
    }
  }
  const handleEffectChange = () => {
    if (props.onEffectChange) props.onEffectChange(textEffects)
    setAnchorEl(null);

  }
  const handleTextEffectChange = (effect, propName, propValue) => {
    //used internally to change an effect prop in the textEffects array
    const newEffects = [...textEffects]
    const index = newEffects.findIndex(eff => eff.effectName === effect.effectName)
    newEffects[index][propName] = propValue
    //console.log(newEffects)
    setTextEffects(newEffects)
  }

  return <>
  <div className='flex flex-row items-center gap-x-2'>
    <Typography noWrap>Text effects:</Typography>
    <Chip sx={{width: '60%'}} label={getLabelTitle(textEffects)} variant="outlined" 
        onClick={handleOpenBuilder} 
        />
  
  </div>
  <Popover      open={openBuilder}
                anchorEl={anchorEl}
                onClose={handleCloseBuilder}
                anchorOrigin={{
                  vertical: 'top',
                }}
      >
    <Paper sx={{ padding: '10px', width: 1000}} elevation={4}>
        <div className='text-ccColorDarkTeal font-poppins'>
            <div className='grid grid-cols-3 gap-2'>
                {effectsGallery.map(eff => <div className={`border ${effectIsUsed(eff) ? 'border-ccColorDarkTeal border-2' : 'border-slate-200'} rounded cursor-pointer hover:bg-slate-200`}
                                            onClick={() => effectIsUsed(eff) ? removeEffect(eff) : addEffect(eff)}>
                        {eff.effectName === 'bevel' ?
                          <TextBevelEffectExample effect={eff} />
                          :
                        <TextEffectExample effect={eff} />                        
                      }
                </div>)}
                <TextGlassEffectExample />
                
            </div>
            <div className='mt-4 grid grid-cols-2'>
              <div className=''>
                <div className='mt-2 font-semibold'>Fine tune properties</div>
                {textEffects.length > 0 && <>
                    {textEffects.map(eff => renderUIForEffect(eff))}
                </>
                }
              </div>
              <div>
                <div>Preview</div>
                <TextNodePreview node={props.currentBlock} effects={textEffects} />
              </div>

            </div>
        </div>
        <div className='flex flex-row mt-2 justify-end'>
            <CcActionButton onClick={handleEffectChange}>Confirm</CcActionButton>

        </div>
    </Paper>
</Popover>
</>

}

export const TextShadowManager = (props) => {
  //NOTE: we only support the shadow effect however we might support
  //multiple effects in the future. FOr this reason we keep textEffects as an array of effects (even if there is only 1 item)
    const shadowDefault = {effectName: 'dropShadow', shadowColor: "#000000",
            shadowOpacity: 0.5, shadowOffsetX: 4, shadowOffsetY: 6,
            shadowBlur: 8}
          

  useEffect(() => {
    setLabelTitle(getLabelTitle(props.currentBlock?.textEffects))
    if (props.currentBlock?.textEffects !== undefined && props.currentBlock?.textEffects.length > 0) setTextEffects(props.currentBlock.textEffects)
    else setTextEffects([{...shadowDefault}])
  }, [props.currentBlock])


  const [textEffects, setTextEffects] = useState([]);
  const [labelTitle, setLabelTitle] = useState('none');
  const [anchorEl, setAnchorEl] = useState(null);

  const openBuilder = Boolean(anchorEl);
  const handleOpenBuilder = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseBuilder = () => {
    if (props.onEffectChange !== undefined) props.onEffectChange(textEffects);
    setAnchorEl(null);
  };
  const handleCancelBuilder = () => {
    setAnchorEl(null)    
  };
  const handleRemoveAndCloseBuilder = () => {
    if (props.onEffectChange !== undefined) props.onEffectChange([]);
    setAnchorEl(null);
    
  };


  const getLabelTitle = (effects) => {
    if (effects == undefined || effects.length === 0) return 'none'

    return <div className='flex flex-row items-center gap-x-4'>
        <div style={{backgroundColor: hexWithAlpha(effects[0].shadowColor, effects[0].shadowOpacity )}} className='w-8 h-5 rounded bg-slate-300'></div>
        <div className='text-xs'>blur: {effects[0].shadowBlur}</div>
    </div>
  }


  const renderUIForEffect = (effect) => {
    switch (effect.effectName)
    {
        case "dropShadow":
            return <div className='text-gray-700 text-sm font-semibold mt-2'>
                    <div className='flex flex-row gap-x-2'>
                      <div>
                        <div>Shadow colour</div>
                        <LionColorPicker color={effect.shadowColor}      
                            onChange={(color) => handleTextEffectChange(effect, 'shadowColor', color)}
                          />
                      </div>
                      <div>
                        <div>Shadow opacity</div>
                        <CcHorizontalSlider min={0} max={1} 
                              value={effect.shadowOpacity}
                              onChange={val => handleTextEffectChange(effect, 'shadowOpacity', val)}
                              />
                      </div>
                    </div>
                    <div className='flex flex-row gap-x-2 mt-2'>
                      <div>
                        <div>Shadow offsetX</div>
                        <div className='w-32'>
                            <InputNumber displayName="" small
                              min={0} max={100} incr={1}
                              onChange={val => handleTextEffectChange(effect, 'shadowOffsetX', val)}
                              value={effect.shadowOffsetX}
                            />
                        </div>
                      </div>
                      <div>
                        <div>Shadow offsetY</div>
                        <div className='w-32'>
                            <InputNumber displayName="" small
                              min={0} max={100} incr={1}
                              onChange={val => handleTextEffectChange(effect, 'shadowOffsetY', val)}
                              value={effect.shadowOffsetY}
                            />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div>Shadow blur</div>
                      <div className='w-32'>
                          <InputNumber displayName="" small
                            min={0} max={100} incr={1}
                            onChange={val => handleTextEffectChange(effect, 'shadowBlur', val)}
                            value={effect.shadowBlur}
                          />
                      </div>
                    </div>
              
            </div>
        break
        default:
            return <></>
    }
  }
  const handleEffectChange = () => {
    if (props.onEffectChange) props.onEffectChange(textEffects)
    setAnchorEl(null);

  }
  const handleTextEffectChange = (effect, propName, propValue) => {
    //used internally to change an effect prop in the textEffects array
    const newEffects = [...textEffects]
    const index = newEffects.findIndex(eff => eff.effectName === effect.effectName)
    newEffects[index][propName] = propValue
    //console.log(newEffects)
    setTextEffects(newEffects)
  }
  function hexWithAlpha(hex, alpha) {
  hex = hex.replace('#', '');

  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  const alphaHex = Math.round(
    Math.max(0, Math.min(1, alpha)) * 255
  ).toString(16).padStart(2, '0').toUpperCase();

  return `#${hex}${alphaHex}`;
}

  return <>
  <div className='flex flex-row items-center gap-x-2'>
    <Typography noWrap>Text shadow:</Typography>
    <Chip sx={{width: '60%'}} label={labelTitle} variant="outlined" 
        onClick={handleOpenBuilder} 
        />
  
  </div>
  <Popover      open={openBuilder}
                anchorEl={anchorEl}
                onClose={handleCloseBuilder}
                anchorOrigin={{
                  vertical: 'top',
                }}
      >
    <Paper sx={{ padding: '10px', width: 1000}} elevation={4}>
        <div className='flex flex-row justify-end'>
          <IconButton onClick={handleCancelBuilder}>
            <Iconify icon={lionIcons.Icon_Actions_Cancel} width={32} />
          </IconButton>
        </div>
        <div className='text-ccColorDarkTeal font-poppins grid grid-cols-2'>
              <div className=''>
                <div className='mt-2 font-semibold'>Fine tune properties</div>
                {textEffects.length > 0 && <>
                    {textEffects.map(eff => renderUIForEffect(eff))}
                </>
                }
              </div>
              <div className='flex flex-col items-center'>
                <div className='font-semibold'>Preview</div>
                <TextNodePreview node={props.currentBlock} effects={textEffects} />
              </div>

        </div>
        <div className='flex flex-row mt-2 justify-end gap-x-2'>
            <CcActionButton onClick={handleRemoveAndCloseBuilder}>Remove</CcActionButton>
            <CcActionButton onClick={handleEffectChange}>Confirm</CcActionButton>

        </div>
    </Paper>
</Popover>
</>

}
